import { useEffect, useState } from "react";
import { Icons, Cascade } from "../lib/icons";
import { Btn, CopyBtn, Modal } from "../lib/primitives";
import { useApiKeys, useCreateApiKey, useOperatorRole, useRevokeApiKey } from "../lib/hooks";
import type { ApiKeyRow } from "../lib/api";

/** Server returns {id, keyPrefix, label, status, expiresAt, createdAt}.
 *  Adapt to the design's row shape so the table renders unchanged.
 *  The auth-server has only sk_ keys today — no pk_ publishable variant.
 */
function adaptKeyRow(r: ApiKeyRow) {
  return {
    id: r.id,
    label: r.label ?? "Untitled key",
    prefix: r.keyPrefix ?? "sk_",
    tail: "•••••",
    fullSample: "",
    createdAt: r.createdAt,
    status: r.status,
  };
}

/** `GET /api/api-keys` returns keys of every status (so does auth-server's own
 *  list endpoint), so the table has to filter. Every row carries a live Revoke
 *  button, which makes a revoked row both misleading and un-revokable: the
 *  server's revoke matches on `status = 'active'` and 404s with "API key not
 *  found or already revoked". The audit log keeps the revocation history.
 */
export function visibleKeyRows(rows: ApiKeyRow[]) {
  return rows.filter((r) => r.status === "active").map(adaptKeyRow);
}

function CreateKeyModal({ open, onClose, onCreate }: { open: boolean; onClose: () => void; onCreate: (config: any) => void }) {
  const [label, setLabel] = useState("");

  useEffect(() => { if (open) setLabel(""); }, [open]);

  return (
    <Modal open={open} onClose={onClose} eyebrow="API keys" title="Create an API key"
           footer={<>
             <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
             <Btn kind="primary" leading={<Icons.Key size={14} />} onClick={() => onCreate({ label: label || "Untitled key" })}>Create key</Btn>
           </>}>
      <div className="ad-stack-lg">
        <div className="ad-field">
          <div className="ad-field-label">Name</div>
          <input className="ad-input" placeholder="For example: Checkout server" value={label} onChange={(e) => setLabel(e.target.value)} autoFocus />
          <div className="ad-field-hint">So you can tell your keys apart. Only your team sees it.</div>
        </div>

        <div style={{ display: "flex", gap: 12, padding: 14, background: "var(--ad-orange-soft)", borderRadius: 14 }}>
          <Icons.Shield size={20} style={{ color: "var(--ad-orange-ink)", flexShrink: 0, marginTop: 1 } as React.CSSProperties} />
          <div style={{ fontSize: 14, color: "var(--ad-fg-2)", lineHeight: 1.55 }}>
            You'll see the key <strong style={{ color: "var(--ad-fg-1)" }}>only once</strong>, on the next screen.
            Have your password manager or secrets store ready.
          </div>
        </div>
      </div>
    </Modal>
  );
}

function RevealKeyModal({ apiKey, onClose }: { apiKey: any; onClose: () => void }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [showSecret, setShowSecret] = useState(true);
  useEffect(() => { if (apiKey) { setAcknowledged(false); setShowSecret(true); } }, [apiKey]);
  if (!apiKey) return null;
  const masked = apiKey.fullSample.slice(0, 8) + "•".repeat(apiKey.fullSample.length - 8);

  return (
    <div className="ad-modal-shroud" onClick={onClose}>
      <div className="ad-modal" style={{ maxWidth: 680 }}
           onClick={(e) => e.stopPropagation()}>
        <div style={{ position: "relative", padding: "32px 32px 8px", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -40, right: -40, opacity: 0.06, pointerEvents: "none" }}>
            <Cascade size={220} />
          </div>
          <div className="ad-eyebrow" style={{ marginBottom: 10 }}>Shown once</div>
          <h2 className="ad-h1" style={{ fontSize: 28, marginBottom: 8 }}>Copy your new key</h2>
          <div className="ad-sub">
            This is the only time you'll see it. We keep a scrambled copy that can't be turned back into the key.
          </div>
        </div>

        <div style={{ padding: "20px 32px" }}>
          <div className="ad-eyebrow-sm" style={{ marginBottom: 8 }}>{apiKey.label}</div>
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "14px 16px",
            border: "1px solid var(--ad-border-2)",
            borderRadius: 10,
            background: "var(--ad-surface-2)",
            fontFamily: "var(--rb-font-mono)",
            fontSize: 13,
            color: "var(--ad-fg-1)",
            wordBreak: "break-all",
          }}>
            <div style={{ flex: 1 }}>{showSecret ? apiKey.fullSample : masked}</div>
            <button className="ad-top-icon-btn" style={{ flexShrink: 0 }} onClick={() => setShowSecret((s) => !s)} title={showSecret ? "Hide" : "Show"}>
              {showSecret ? <Icons.EyeOff /> : <Icons.Eye />}
            </button>
            <CopyBtn text={apiKey.fullSample} label="Copy" />
          </div>
          <div style={{ marginTop: 10, fontSize: 11, color: "var(--ad-fg-3)", fontFamily: "var(--rb-font-mono)" }}>
            created {new Date(apiKey.createdAt).toLocaleString()}
          </div>
        </div>

        <div style={{ padding: "8px 32px 16px" }}>
          <div className="ad-eyebrow-sm" style={{ marginBottom: 10 }}>Try it</div>
          <div className="ad-code">
            <div className="ad-code-bar">
              <div className="ad-code-bar-l">
                <span className="ad-code-tab is-active">curl</span>
              </div>
              <CopyBtn text={`curl -X POST https://api.authensee.com/v1/sessions \\\n  -H "x-api-key: ${apiKey.fullSample}" \\\n  -d '{ "scope": "authenticate", "externalUserId": "user_123" }'`} small />
            </div>
            <div className="ad-code-body">{
`$ curl -X POST https://api.authensee.com/v1/sessions \\
    -H "x-api-key: `}<span style={{ color: "var(--rb-orange)" }}>{apiKey.fullSample.slice(0, 14)}...</span>{`" \\
    -d '{ "scope": "authenticate", "externalUserId": "user_123" }'`}</div>
          </div>
        </div>

        <div style={{ padding: "8px 32px 4px" }}>
          <label className="ad-check" data-checked={acknowledged} onClick={() => setAcknowledged((a) => !a)}>
            <div className="ad-check-box" />
            <div className="ad-check-content">
              <div className="ad-check-title">I've saved the key somewhere safe</div>
              <div className="ad-check-sub">AuthenSee can't show it again once this window is closed.</div>
            </div>
          </label>
        </div>

        <div className="ad-modal-foot">
          <Btn kind="ghost" onClick={() => window.open("https://docs.authensee.com/", "_blank")} leading={<Icons.Book size={14} />}>Setup guide</Btn>
          <Btn kind="primary" disabled={!acknowledged} onClick={onClose} >Done</Btn>
        </div>
      </div>
    </div>
  );
}

export function Keys() {
  // Viewers can list keys but not mint/revoke (#53); the server enforces it.
  const { canWrite } = useOperatorRole();
  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealKey, setRevealKey] = useState<any>(null);
  const [revokeKey, setRevokeKey] = useState<any>(null);

  const { data: serverKeys } = useApiKeys();
  const createKey = useCreateApiKey();
  const revoke = useRevokeApiKey();

  const keys = visibleKeyRows(serverKeys ?? []);

  const handleCreate = async (config: any) => {
    setCreateOpen(false);
    setCreateError(null);
    try {
      const result = await createKey.mutateAsync(config.label || `Untitled key`);
      setRevealKey({
        id: result.keyId,
        label: config.label || "Untitled key",
        prefix: result.apiKey.slice(0, 8),
        tail: "•" + result.apiKey.slice(-5),
        fullSample: result.apiKey,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      // Surface the real failure. We never fabricate a key — a synthetic
      // sk_live_-looking string would be impossible to distinguish from a
      // real one and could mislead operators.
      setCreateError(err instanceof Error ? err.message : "Couldn't create the key. Try again.");
    }
  };

  const handleRevoke = async (k: any) => {
    try {
      await revoke.mutateAsync(k.id);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : "Couldn't revoke the key. Try again.");
    }
    setRevokeKey(null);
  };

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Set up</div>
          <h1 className="ad-h1">API keys</h1>
          <div className="ad-sub">
            Your server uses an API key (<span className="ad-mono">sk_…</span>) to start sign-ins. Keep it on your
            server, never in your app or website code. Each key is shown once, when you create it.
          </div>
        </div>
        <div className="ad-page-head-r">
          <Btn kind="ghost" leading={<Icons.Book size={14} />} onClick={() => window.open("https://docs.authensee.com/", "_blank")}>Docs</Btn>
          {canWrite && (
            <Btn kind="primary" leading={<Icons.Plus size={14} />} onClick={() => setCreateOpen(true)}>Create key</Btn>
          )}
        </div>
      </div>

      <div className="ad-card-flat" style={{ padding: 0, overflow: "hidden" }}>
        <table className="ad-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Key</th>
              <th>Created</th>
              <th style={{ textAlign: "right", width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>{k.label}</div>
                  <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>{k.id}</div>
                </td>
                <td>
                  <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 8px", border: "1px solid var(--ad-hairline)", borderRadius: 6, background: "var(--ad-surface-0)" }}>
                    <span className="ad-mono-or">{k.prefix}</span>
                    <span style={{ fontFamily: "var(--rb-font-mono)", color: "var(--ad-fg-4)", fontSize: 12 }}>{k.tail}</span>
                  </div>
                </td>
                <td className="muted" style={{ fontSize: 12 }}>{new Date(k.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                <td style={{ textAlign: "right" }}>
                  {canWrite && (
                    <button className="ad-top-icon-btn" style={{ width: 28, height: 28, color: "var(--ad-error)" }} title="Revoke" onClick={() => setRevokeKey(k)}><Icons.Trash size={14} /></button>
                  )}
                </td>
              </tr>
            ))}
            {keys.length === 0 && (
              <tr>
                <td colSpan={4} style={{ padding: 0 }}>
                  <div className="ad-empty" style={{ border: 0, margin: 0 }}>
                    <Cascade size={48} />
                    <div className="ad-empty-title">No API keys yet</div>
                    <div className="ad-empty-sub">
                      {canWrite
                        ? "Create a key so your server can start sign-ins."
                        : "An owner or admin can create the first key."}
                    </div>
                    {canWrite && (
                      <Btn kind="primary" leading={<Icons.Plus size={14} />} onClick={() => setCreateOpen(true)}>Create key</Btn>
                    )}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      <CreateKeyModal open={createOpen} onClose={() => setCreateOpen(false)} onCreate={handleCreate} />

      {createError && (
        <div
          role="alert"
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            maxWidth: 360,
            padding: "12px 16px",
            background: "var(--ad-surface-1)",
            border: "1px solid var(--ad-border-1)",
            borderRadius: 14,
            boxShadow: "var(--ad-card-shadow)",
            color: "var(--ad-fg-1)",
            fontSize: 13,
            zIndex: 100,
          }}
        >
          <strong>Something went wrong:</strong>
          <div style={{ marginTop: 4, color: "var(--ad-fg-2)" }}>{createError}</div>
          <button
            type="button"
            onClick={() => setCreateError(null)}
            style={{
              marginTop: 8,
              background: "transparent",
              border: 0,
              color: "var(--ad-fg-3)",
              fontSize: 12,
              cursor: "pointer",
              padding: 0,
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* The reveal moment — hero */}
      <RevealKeyModal apiKey={revealKey} onClose={() => setRevealKey(null)} />

      {/* Revoke confirm */}
      <Modal open={!!revokeKey} onClose={() => setRevokeKey(null)} eyebrow="API keys" title="Revoke this key?"
             footer={<>
               <Btn kind="ghost" onClick={() => setRevokeKey(null)}>Cancel</Btn>
               <Btn kind="danger" leading={<Icons.Trash size={14} />} onClick={() => handleRevoke(revokeKey)}>Revoke key</Btn>
             </>}>
        {revokeKey && (
          <div className="ad-stack-md">
            <div className="ad-sub">
              <strong style={{ color: "var(--ad-fg-1)" }}>{revokeKey.label}</strong> will stop working immediately.
              Requests made with it will get <span className="ad-mono">401 unauthorized</span>.
              You can't undo this.
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: "var(--ad-surface-3)", borderRadius: 6 }}>
              <span className="ad-mono-or">{revokeKey.prefix}</span>
              <span style={{ fontFamily: "var(--rb-font-mono)", color: "var(--ad-fg-4)", fontSize: 12 }}>{revokeKey.tail}</span>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
